/**
 * Fantasy Console 0 - Header Component
 */

import { useState, useRef, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useCart } from '../../context/CartContext'
import { useToast } from '../../context/ToastContext'
import { useAuth } from '../../context/AuthContext'
import { useAuthManager } from '../Auth/AuthManager'
import { exportCartAsFile, importCartFromFile } from '../../utils/storage'
import { countTokensInFiles, MAX_TOKENS } from '../../utils/tokenCounter'
import { NewCartModal } from '../Modals/NewCartModal'
import { EditUsernameModal } from '../Modals/EditUsernameModal'
import { ConfirmDialog } from '../UI/ConfirmDialog'
import { ViewId } from '../../types'
import styles from './Header.module.css'
import {
  ChevronDownIcon,
  CloudUploadIcon,
  EditIcon,
  ExportIcon,
  ForkIcon,
  ImportIcon,
  NewIcon,
  ShareIcon,
  SignOutIcon,
  SpinnerIcon,
  UserIcon,
} from '../shared/icons'

export function Header() {
  const {
    cart,
    isDirty,
    loadCart,
    saveToCloud,
    forkCurrentCart,
    cloudSaveEnabled,
    hasUnsavedCloudChanges,
    updateCloudSaveHash,
    activeView,
    lastEditorTab,
    setActiveTab,
  } = useCart()
  const { user, username, signOut, loading: authLoading } = useAuth()
  const { openSignIn } = useAuthManager()
  const { success, error, info, warning } = useToast()
  const [isNewCartModalOpen, setIsNewCartModalOpen] = useState(false)
  const [isEditUsernameModalOpen, setIsEditUsernameModalOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showForkConfirm, setShowForkConfirm] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)

  const tokenCounts = useMemo(
    () => countTokensInFiles(cart.codeFiles),
    [cart.codeFiles],
  )
  const isOverTokenLimit = tokenCounts.total > MAX_TOKENS

  // Check if user is the owner of the current cart
  const isCartOwner = !cart.userId || (user && cart.userId === user.id)
  const neverSavedToCloud = user && !cart.id

  // Close user menu on outside click
  useEffect(() => {
    if (!showUserMenu) return

    const handleClick = (e: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(e.target as Node)
      ) {
        setShowUserMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showUserMenu])

  const handleNewCart = () => {
    if (isDirty) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to create a new cart?',
      )
      if (!confirmed) return
    }
    setIsNewCartModalOpen(true)
  }

  const handleSaveToCloud = async () => {
    if (!cloudSaveEnabled) {
      info('Cloud storage is not configured.')
      return
    }

    if (!user) {
      openSignIn()
      return
    }

    if (isSaving) return

    if (isOverTokenLimit) {
      warning(
        `Cart exceeds the ${MAX_TOKENS} token limit. Reduce your code to save to cloud.`,
      )
      return
    }

    // If viewing someone else's cart, prompt to fork
    if (!isCartOwner) {
      setShowForkConfirm(true)
      return
    }

    try {
      setIsSaving(true)
      await saveToCloud()
      updateCloudSaveHash()
      success('Cart saved to cloud')
    } catch (err) {
      console.error('Failed to save cart:', err)
      error(err instanceof Error ? err.message : 'Failed to save cart to cloud')
    } finally {
      setIsSaving(false)
    }
  }

  const handleForkAndSave = async () => {
    setShowForkConfirm(false)

    if (!cloudSaveEnabled || !user) return
    if (isSaving) return

    if (isOverTokenLimit) {
      warning(
        `Cart exceeds the ${MAX_TOKENS} token limit. Reduce your code to save to cloud.`,
      )
      return
    }

    try {
      setIsSaving(true)
      await forkCurrentCart()
      updateCloudSaveHash()
      success('Cart forked and saved to your account!')
    } catch (err) {
      console.error('Failed to fork cart:', err)
      error(err instanceof Error ? err.message : 'Failed to fork cart')
    } finally {
      setIsSaving(false)
    }
  }

  const handleExport = async () => {
    try {
      const confirmed = await exportCartAsFile(cart)
      if (confirmed) success('Cart exported successfully')
    } catch (err) {
      error('Failed to export cart')
    }
  }

  const handleImportClick = () => {
    if (isDirty) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to import a new cart?',
      )
      if (!confirmed) return
    }
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      const importedCart = await importCartFromFile(file)
      loadCart(importedCart)
      success(`Loaded cart: ${importedCart.metadata.title}`)
    } catch (err) {
      error(err instanceof Error ? err.message : 'Failed to import cart')
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut()
      setShowUserMenu(false)
      success('Signed out successfully')
    } catch (err) {
      error('Failed to sign out')
    }
  }

  const handleShare = async () => {
    if (!cart.id) return
    const url = `${window.location.origin}/play/${cart.id}`
    try {
      await navigator.clipboard.writeText(url)
      success('Share link copied to clipboard!')
    } catch {
      error('Failed to copy link')
    }
  }

  const handleViewChange = (view: ViewId) => {
    if (view === 'editor') {
      setActiveTab(lastEditorTab)
    } else if (view === 'library') {
      setActiveTab('library')
    } else {
      setActiveTab('play')
    }
  }

  return (
    <header className={styles.header}>
      <div className={styles.branding}>
        <Link to='/welcome' className={styles.logo}>
          FC0
        </Link>
        <span className={styles.title}>{cart.metadata.title}</span>
      </div>

      {/* Centered View Toggle */}
      <div className={styles.viewToggle}>
        <button
          className={`${styles.toggleSegment} ${activeView === 'editor' ? styles.toggleActive : ''}`}
          onClick={() => handleViewChange('editor')}
        >
          Editor
        </button>
        <button
          className={`${styles.toggleSegment} ${activeView === 'library' ? styles.toggleActive : ''}`}
          onClick={() => handleViewChange('library')}
        >
          Library
        </button>
        <button
          className={`${styles.toggleSegment} ${activeView === 'play' ? styles.toggleActive : ''}`}
          onClick={() => handleViewChange('play')}
        >
          Play
        </button>
      </div>

      <div className={styles.actions}>
        <button
          className={`btn btn-sm ${hasUnsavedCloudChanges || neverSavedToCloud || !isCartOwner ? 'btn-primary' : 'btn-ghost'}`}
          onClick={handleSaveToCloud}
          disabled={isSaving}
          title={
            !user
              ? 'Sign in to save to cloud'
              : !isCartOwner
                ? 'Fork this cart to save your changes'
                : 'Save to Cloud (Ctrl + S)'
          }
        >
          {isSaving ? (
            <SpinnerIcon />
          ) : !isCartOwner ? (
            <ForkIcon />
          ) : (
            <CloudUploadIcon />
          )}
          <span className='desktop-only'>
            {!user
              ? 'Sign in to Save'
              : isSaving
                ? 'Saving...'
                : !isCartOwner
                  ? 'Fork & Save'
                  : 'Save to Cloud'}
          </span>
        </button>

        {cart.id && (
          <button
            className='btn btn-ghost btn-sm'
            onClick={handleShare}
            title='Copy share link'
          >
            <ShareIcon />
          </button>
        )}

        <button
          className='btn btn-ghost btn-sm'
          onClick={handleNewCart}
          title='New Cart (Ctrl + N)'
        >
          <NewIcon />
          {/* <span className="desktop-only">New</span> */}
        </button>

        <button
          className='btn btn-ghost btn-sm'
          onClick={handleImportClick}
          title='Import Cart (Ctrl + O)'
        >
          <ImportIcon size={16} />
          {/* <span className="desktop-only">Import</span> */}
        </button>

        <button
          className='btn btn-ghost btn-sm'
          onClick={handleExport}
          title='Export Cart (Ctrl + E)'
        >
          <ExportIcon size={16} />
          {/* <span className="desktop-only">Export</span> */}
        </button>

        {/* User Menu */}
        {!authLoading &&
          (user ? (
            <div className={styles.userMenuContainer} ref={userMenuRef}>
              <button
                className={styles.userButton}
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <UserIcon />
                {/* <span className="desktop-only">{username || 'User'}</span> */}
                <ChevronDownIcon />
              </button>

              {showUserMenu && (
                <div className={styles.userMenu}>
                  <div className={styles.userInfo}>
                    <span className={styles.userName}>
                      {username || 'User'}
                    </span>
                    <span className={styles.userEmail}>{user.email}</span>
                  </div>
                  <div className={styles.menuDivider} />
                  <button
                    className={styles.menuItem}
                    onClick={() => {
                      setShowUserMenu(false)
                      setIsEditUsernameModalOpen(true)
                    }}
                  >
                    <EditIcon />
                    Change Username
                  </button>
                  <button className={styles.menuItem} onClick={handleSignOut}>
                    <SignOutIcon />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className='btn btn-ghost btn-sm' onClick={openSignIn}>
              <UserIcon />
              {/* <span className="desktop-only">Sign In</span> */}
            </button>
          ))}
      </div>

      <input
        ref={fileInputRef}
        type='file'
        accept='.json'
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <NewCartModal
        isOpen={isNewCartModalOpen}
        onClose={() => setIsNewCartModalOpen(false)}
      />

      <ConfirmDialog
        isOpen={showForkConfirm}
        title='Fork This Cart?'
        message="This cart belongs to another user. To save your changes, you'll need to fork it to your own account. The original cart will remain unchanged."
        confirmLabel='Fork & Save'
        confirmVariant='primary'
        onConfirm={handleForkAndSave}
        onCancel={() => setShowForkConfirm(false)}
      />

      <EditUsernameModal
        isOpen={isEditUsernameModalOpen}
        onClose={() => setIsEditUsernameModalOpen(false)}
      />
    </header>
  )
}
